import React, { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import { SubmitHandler } from 'react-hook-form'

import {
  IResponse,
  IErrorResponse,
  IPageDetailsType,
  IReserveNowResponse,
  TSearchReservationSchema,
} from '@/types'
import {
  confirmedPath,
  joinArray,
  searchReservationForm,
  setStorageValue,
} from '@/utils'
import { Button, Form, TextInput } from '@/components'
import { useHookForm, useLanguages } from '@/hooks'
import { CONFIRMED_KEY } from '@/constants'
import { useGetReservation } from '@/apis'

export const SearchForm: React.FC<IPageDetailsType> = ({ description }) => {
  const params = useSearchParams()
  const { t, appLang } = useLanguages()
  const [errorMessage, setErrorMessage] = useState<string>()

  const surname = params.get('lastName')
  const givenName = params.get('firstName')
  const reservationNumber = params.get('id')

  const onFindReservation = useCallback(
    (data: IResponse<IReserveNowResponse>) => {
      setStorageValue(CONFIRMED_KEY, { ...data.data, modifying: true })
      setErrorMessage(undefined)
      const timer = setTimeout(() => {
        window.location.replace(
          joinArray('', appLang === 'en' ? undefined : appLang, confirmedPath),
        )
        clearTimeout(timer)
      }, 10)
    },
    [appLang],
  )

  const onFail = useCallback((error: IErrorResponse) => {
    setErrorMessage(error?.response?.data?.message)
  }, [])

  const [searchReservation] = useGetReservation(onFindReservation, onFail)

  const {
    handler: {
      reset,
      register,
      handleSubmit,
      formState: { errors },
    },
  } = useHookForm(searchReservationForm)

  const onSubmit: SubmitHandler<TSearchReservationSchema> = useCallback(
    payload => {
      searchReservation({
        queryParams: Object.keys(payload || {}).map(key => ({
          // @ts-ignore
          [key]: payload[key],
        })),
        pathParams: { lang: appLang },
      })
    },
    [appLang, searchReservation],
  )

  useEffect(() => {
    if (surname && givenName && reservationNumber) {
      const data = {
        surname,
        givenName,
        reservationNumber,
      }
      reset(data)
      onSubmit(data)
    }
  }, [onSubmit, reset, surname, givenName, reservationNumber])

  return (
    <div className="theme--light-blue padding-vertical--small view_section">
      <div className="row">
        <div className="column small-12 medium-6">
          <Form onSubmit={handleSubmit(onSubmit)}>
            {!!errorMessage && <div className="bg-yellow">{errorMessage}</div>}
            <div className="flex-container flex-row-wrap view_section_left">
              <TextInput
                required
                className="field"
                label={t('firstName')}
                {...register('givenName')}
                error={errors?.givenName?.message}
              />
              <TextInput
                required
                className="field"
                label={t('lastName')}
                {...register('surname')}
                error={errors?.surname?.message}
              />
              <TextInput
                required
                className="field"
                label={t('confirmationNumber')}
                error={errors?.surname?.message}
                {...register('reservationNumber')}
              />
              <div className="small-12 medium-2_5">
                <Button
                  type="submit"
                  className="btn-block reservation_form_submit"
                >
                  <span>{t('search')}</span>
                </Button>
              </div>
            </div>
          </Form>
        </div>
        <div className="column small-12 medium-6">
          <div className="flex-container align-middle flex-row-wrap view_section_right">
            <div className="small-12 medium-3">
              <img
                alt=""
                src="/assets/img/illustration-secondary-reservations@3x.png"
              />
            </div>

            <div className="small-12 medium-9">
              <div className="white-color">
                <div dangerouslySetInnerHTML={{ __html: description ?? '' }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
